import { useEffect, useState } from 'react';

import { EProjectSection, trackEventV2 } from '@/services/analytics/trackEventV2';
import { getODAnonymousId } from '@/services/segment';
import { isSSR } from '@/utility/isSSR';

interface IFlagsVariant {
  key: string;
  value: unknown;
  experiment_id?: string;
}

// Only the parts of the SDK this reader uses; get_all_variants skips the SDK's own exposure tracking.
interface IFlagsMixpanel {
  register: (properties: Record<string, unknown>) => void;
  flags: {
    get_all_variants: () => Promise<Map<string, IFlagsVariant> | Record<string, IFlagsVariant>>;
    update_context: (context: Record<string, unknown>) => Promise<void>;
  };
}

// Outdoorsy's Mixpanel projects use Original ID Merge, where an anonymous visitor's distinct_id is the bare id.
const toIdentity = (anonId: string) => ({ distinct_id: anonId, $device_id: anonId });
const toFlagContext = (anonId: string) => ({ distinct_id: anonId, device_id: anonId });

const ENABLED_VALUES: ReadonlySet<unknown> = new Set([true, 'on', 'enabled']);

let mixpanel: IFlagsMixpanel | null = null;
let setupPromise: Promise<void> | null = null;
let identifiedAnonId: string | null = null;
// Kept even before setup, so an id change that lands while the SDK is still loading isn't lost.
let latestAnonId: string | null = null;

const moveToAnonId = (instance: IFlagsMixpanel, anonId: string) => {
  identifiedAnonId = anonId;
  instance.register(toIdentity(anonId));
  instance.flags.update_context(toFlagContext(anonId)).catch(() => undefined);
};

const setup = async () => {
  const token = process.env.mixpanelToken;
  if (!token) return;
  try {
    const anonId = await getODAnonymousId();
    const sdk = (await import('mixpanel-browser')).default;
    // Flag reads only: identity belongs to Segment, so this instance never identifies, tracks or persists.
    mixpanel = sdk.init(
      token,
      {
        flags: { context: toFlagContext(anonId) },
        track_pageview: false,
        autocapture: false,
        disable_persistence: true,
      },
      'od_flags',
    ) as unknown as IFlagsMixpanel;
    mixpanel.register(toIdentity(anonId));
    identifiedAnonId = anonId;
    if (latestAnonId && latestAnonId !== anonId) moveToAnonId(mixpanel, latestAnonId);
  } catch {
    mixpanel = null;
  }
};

const ensureSetup = () => {
  if (isSSR()) return Promise.resolve();
  setupPromise ??= setup();
  return setupPromise;
};

/** Follow an anonymous-id change (e.g. logout) without loading the SDK before any flag is read. */
export const setMixpanelFlagUser = (anonId: string) => {
  latestAnonId = anonId;
  if (!mixpanel || anonId === identifiedAnonId) return;
  moveToAnonId(mixpanel, anonId);
};

const getVariant = async (flag: string): Promise<IFlagsVariant | null> => {
  await ensureSetup();
  if (!mixpanel) return null;
  try {
    const variants = await mixpanel.flags.get_all_variants();
    return (variants instanceof Map ? variants.get(flag) : variants?.[flag]) ?? null;
  } catch {
    return null;
  }
};

const exposedThisLoad = new Set<string>();

// Exposure goes through Segment so it shares identity with the conversion events it's joined against.
const trackExposure = (flag: string, variant: IFlagsVariant) => {
  if (!variant.experiment_id) return;
  const key = `${flag}:${variant.key}`;
  if (exposedThisLoad.has(key)) return;
  exposedThisLoad.add(key);
  trackEventV2('$experiment_started', EProjectSection.MARKETPLACE, {
    'Experiment name': flag,
    'Variant name': variant.key,
    $experiment_type: 'feature_flag',
    $experiment_id: variant.experiment_id,
  });
};

// Nothing is read or exposed until shouldRun, so callers can defer bucketing until the feature can render.
export const useMixpanelFlag = (flag: string, { shouldRun = true } = {}): boolean => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!shouldRun) return;
    let active = true;
    getVariant(flag).then(variant => {
      if (!active) return;
      setEnabled(ENABLED_VALUES.has(variant?.value));
      if (variant) trackExposure(flag, variant);
    });
    return () => {
      active = false;
    };
  }, [flag, shouldRun]);

  return shouldRun && enabled;
};
